<template>
    <div>
        <modal id="place-branch-form" ref="branchForm" @close="closeBranchModal">
            <template slot="title">{{ t('places-branch_form-title') }}</template>
            <template slot="body">
                <form
                    id="branch-full-form"
                    method="post"
                    action=""
                    @submit.prevent
                >
                    <div class="field">
                        <label>{{ t('places-branch_form-title_label') }}</label>
                        <input
                            type="text"
                            class="form-control"
                            required
                            v-model="branchForm.title"
                            @input="clearCustomValidity($event); validateTitle()"
                            :title="t('places-branch_form-title_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-title_required')"
                        />
                        <span v-if="errors.title" class="error">
                            {{ errors.title }}
                        </span>
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-country') }}</label>
                        <select
                            required
                            v-model="branchForm.country_code"
                            class="form-control"
                            :title="t('places-branch_form-country_title')"
                        >
                            <option
                                v-for="countryOption in countryOptions"
                                :key="countryOption.value"
                                :value="countryOption.value"
                            >{{ countryOption.label }}</option>
                        </select>
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-zip_code') }}</label>
                        <input
                            v-if="isBrazil"
                            type="text"
                            required
                            class="form-control"
                            v-model="branchForm.zip_code"
                            v-mask="'#####-###'"
                            @input="clearCustomValidity($event); validateZipCode()"
                            @blur="fetchAddress"
                            :title="t('places-branch_form-zip_code_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-zip_code_required')"
                        />
                        <input
                            v-else
                            type="text"
                            required
                            class="form-control"
                            v-model="branchForm.zip_code"
                            @input="clearCustomValidity($event); validateZipCode()"
                            :title="t('places-branch_form-zip_code_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-zip_code_required')"
                        />
                        <span v-if="errors.zip_code" class="error">
                            {{ errors.zip_code }}
                        </span>
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-address') }}</label>
                        <input
                            type="text"
                            class="form-control"
                            required
                            v-model="branchForm.address_1"
                            @input="clearCustomValidity($event); validateAddress1()"
                            :title="t('places-branch_form-address_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-address_required')"
                        />
                        <span v-if="errors.address_1" class="error">
                            {{ errors.address_1 }}
                        </span>
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-address_2') }}</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="branchForm.address_2"
                        />
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-zone') }}</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="branchForm.zone"
                        />
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-city') }}</label>
                        <input
                            type="text"
                            class="form-control"
                            required
                            v-model="branchForm.city"
                            @input="clearCustomValidity($event); validateCity()"
                            :title="t('places-branch_form-city_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-city_required')"
                        />
                        <span v-if="errors.city" class="error">
                            {{ errors.city }}
                        </span>
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-state') }}</label>
                        <select
                            v-if="isBrazil"
                            v-model="branchForm.state"
                            class="form-control"
                            required
                            @change="clearCustomValidity($event); validateState()"
                            :title="t('places-branch_form-state_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-state_required')"
                        >
                            <option
                                v-for="stateOption in stateOptions"
                                :key="stateOption.value"
                                :value="stateOption.value"
                            >{{ stateOption.label }}</option>
                        </select>
                        <input
                            v-else
                            type="text"
                            v-model="branchForm.state"
                            class="form-control"
                            required
                            @input="clearCustomValidity($event); validateState()"
                            :title="t('places-branch_form-state_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-state_required')"
                        />
                        <span v-if="errors.state" class="error">
                            {{ errors.state }}
                        </span>
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-phone') }}</label>
                        <kv-phone-input
                            :placeholder="t('places-branch_form-phone_placeholder')"
                            :default-country="branchForm.country_code"
                            v-model="branchForm.phone"
                            @input="validatePhone"
                        />
                        <span v-if="errors.phone" class="error">
                            {{ errors.phone }}
                        </span>
                    </div>
                    <div class="field" v-if="activeBranch">
                        <label>{{ t('places-branch_form-active') }}</label>
                        <select
                            v-model="branchForm.active"
                            class="form-control"
                            required
                            @change="clearCustomValidity($event); validateActive()"
                            :title="t('places-branch_form-active_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-branch_form-validation-active_required')"
                        >
                            <option value="0">{{ t('places-branch_form-no') }}</option>
                            <option value="1">{{ t('places-branch_form-yes') }}</option>
                        </select>
                        <span v-if="errors.active" class="error">
                            {{ errors.active }}
                        </span>
                    </div>
                    <div class="field">
                        <label>Razão Social</label>
                        <input type="text" class="form-control" v-model="branchForm.legal_name" />
                    </div>
                    <div class="field">
                        <label>Documento Fiscal</label>
                        <input type="text" class="form-control" v-model="branchForm.tax_id" placeholder="Ex: CNPJ, VAT, EIN..." />
                    </div>
                    <div class="field">
                        <label>{{ t('places-branch_form-email') }}</label>
                        <input
                            type="email"
                            class="form-control"
                            v-model="branchForm.email"
                            :title="t('places-branch_form-email_title')"
                            @input="clearCustomValidity($event); validateEmail()"
                        />
                        <span v-if="errors.email" class="error">
                            {{ errors.email }}
                        </span>
                    </div>
                    <div class="field" v-if="$module('whatsapp')">
                        <label>{{ t('places-branch_form-whatsapp') }}</label>
                        <select
                            v-model="branchForm.whatsapp_places_senders_id"
                            class="form-control"
                        >
                            <option :value="null">
                                {{ t('places-branch_form-no_whatsapp_instance') }}
                            </option>
                            <option
                                v-for="sender in whatsappSenders"
                                :value="sender.id"
                                :key="sender.id"
                            >
                                {{ sender.alias }}
                            </option>
                        </select>
                    </div>
                    <input
                        type="submit"
                        ref="branchSubmit"
                        style="visibility: hidden"
                    />
                </form>
            </template>
            <template slot="footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="close()"
                >
                    {{ t('places-branch_form-cancel') }}
                </button>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="saveBranch"
                    :disabled="savingBranch"
                >
                    {{ t('places-branch_form-confirm') }}
                </button>
            </template>
        </modal>
    </div>
</template>
<style lang="less" scoped>
.error {
    color: red;
    font-size: 0.9em;
}
div.field {
    margin-bottom: 12px;
}
</style>
<script>
import keviApi from "../../../keviApi";
import { mapState, mapActions, mapMutations } from "vuex";
import _ from "lodash";
import KvPhoneInput from "../../../components/kv-phone-input/kv-phone-input.vue";
import { isValidPhone } from "../../../helpers/phone";
const defaultForm = {
    id: null,
    places_id: null,
    title: null,
    legal_name: null,
    tax_id: null,
    address_1: null,
    address_2: null,
    zone: null,
    city: null,
    state: null,
    zip_code: null,
    country_code: "BR",
    phone: null,
    email: null,
    active: null,
    whatsapp_places_senders_id: null,
};
export default {
    components: { KvPhoneInput },
    data() {
        return {
            savingBranch: false,
            branchForm: _.cloneDeep(defaultForm),
            errors: {
                title: null,
                address_1: null,
                city: null,
                state: null,
                active: null,
                zip_code: null,
                email: null,
                phone: null,
            },
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.placeDetails.users,
            branches: (state) => state.placeDetails.branches,
            activeBranch: (state) => state.placeDetails.activeBranch,
            modalVisible: (state) => state.placeDetails.modalVisible,
            placeId: (state) => state.placeDetails.id,
            senders: (state) => state.placeDetails.senders,
            placeDetails: (state) => state.placeDetails.placeDetails,
        }),
        isBrazil() {
            return this.branchForm.country_code === "BR";
        },
        defaultBranchCountry() {
            return this.placeDetails.country_code || "BR";
        },
        countryOptions() {
            return [
                "BR",
                "US",
                "CA",
                "AR",
                "UY",
                "PY",
                "CL",
                "CO",
                "PE",
                "MX",
                "PT",
                "ES",
                "FR",
                "DE",
                "IT",
                "GB",
            ].map((value) => ({
                value,
                label: this.t(`places-form-countries-${value}`),
            }));
        },
        whatsappSenders() {
            return this.senders.filter((sender) => sender.channel === "whatsapp");
        },
        stateOptions() {
            return [
                "AC",
                "AL",
                "AP",
                "AM",
                "BA",
                "CE",
                "DF",
                "ES",
                "GO",
                "MA",
                "MT",
                "MS",
                "MG",
                "PA",
                "PB",
                "PR",
                "PE",
                "PI",
                "RJ",
                "RN",
                "RS",
                "RO",
                "RR",
                "SC",
                "SP",
                "SE",
                "TO",
            ].map((value) => ({
                value,
                label: this.t(`places-form-states-${value}`),
            }));
        },
    },
    methods: {
        clearCustomValidity(event) {
            event.target.setCustomValidity("");
        },
        setCustomValidity(event, key) {
            event.target.setCustomValidity(this.t(key));
        },
        closeBranchModal() {
            if (this.modalVisible === "branch-form") {
                this.close();
            }
        },
        resetErrors() {
            this.errors = {
                title: null,
                address_1: null,
                city: null,
                state: null,
                active: null,
                zip_code: null,
                email: null,
                phone: null,
            };
        },
        validateTitle() {
            if (!this.branchForm.title) {
                this.errors.title = this.t("places-branch_form-validation-title_required");
            } else {
                this.errors.title = null;
            }
        },
        validateAddress1() {
            if (!this.branchForm.address_1) {
                this.errors.address_1 = this.t("places-branch_form-validation-address_required");
            } else {
                this.errors.address_1 = null;
            }
        },
        validateCity() {
            if (!this.branchForm.city) {
                this.errors.city = this.t("places-branch_form-validation-city_required");
            } else {
                this.errors.city = null;
            }
        },
        validateZipCode() {
            if (!this.branchForm.zip_code) {
                this.errors.zip_code = this.t("places-branch_form-validation-zip_code_required");
            } else {
                this.errors.zip_code = null;
                if (!this.isBrazil) {
                    return;
                }
                const digits = this.branchForm.zip_code.replace(/\D/g, "");
                if (digits.length === 8) {
                    this.fetchAddress();
                }
            }
        },
        async fetchAddress() {
            if (!this.isBrazil) return;
            const cep = this.branchForm.zip_code ? this.branchForm.zip_code.replace(/\D/g, "") : "";
            if (cep.length !== 8) return;

            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();

                if (data.erro) {
                    this.errors.zip_code = "CEP não encontrado.";
                    return;
                }

                if (data.logradouro) this.branchForm.address_1 = data.logradouro;
                if (data.bairro) this.branchForm.zone = data.bairro;
                if (data.localidade) this.branchForm.city = data.localidade;
                if (data.uf) this.branchForm.state = data.uf;

                this.errors.zip_code = null;
                this.validateAddress1();
                this.validateCity();
                this.validateState();
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        },
        validateActive() {
            if (this.branchForm.active === "") {
                this.errors.active = this.t("places-branch_form-validation-active_required");
            } else {
                this.errors.active = null;
            }
        },
        validateState() {
            if (!this.branchForm.state) {
                this.errors.state = this.t("places-branch_form-validation-state_required");
            } else {
                this.errors.state = null;
            }
        },
        validateEmail() {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (this.branchForm.email) {
                if (!emailPattern.test(this.branchForm.email)) {
                    this.errors.email = this.t("places-branch_form-validation-email_valid");
                }
                else {
                    this.errors.email = null;
                }
            } else {
                this.errors.email = null;
            }
        },
        validatePhone() {
            if (this.branchForm.phone) {
                if (!isValidPhone(this.branchForm.phone, this.branchForm.country_code)) {
                    this.errors.phone = this.t("places-branch_form-validation-phone_format");
                } else {
                    this.errors.phone = null;
                }
            } else {
                this.errors.phone = null;
            }
        },
        ...mapMutations({
            close: "placeDetails/closeModal",
        }),
        ...mapActions({
            loadBranches: "placeDetails/loadBranches",
        }),
        validateAll() {
            this.validateTitle();
            this.validateAddress1();
            this.validateCity();
            this.validateState();
            this.validateZipCode();
            this.validateEmail();
            this.validatePhone();

            return !this.hasErrors();
        },
        hasErrors() {
            return  Object.values(this.errors).some(error => error !== null);
        },
        saveBranch: function () {
            if (!this.validateAll()) {
                this.$store.dispatch("notification/warning", {
                    text: this.t("places-branch_form-notification-fill_fields"),
                    duration: 5000,
                });
                return;
            }

            let form = document.getElementById("branch-full-form");
            this.$refs.branchSubmit.click();
            if (form.checkValidity()) {
                this.savingBranch = true;
                let branchData = { ...this.branchForm };
                branchData.places_id = this.placeId;
                branchData.active =  this.activeBranch  ? branchData.active : 1;
                branchData.country = this.countryLabelFor(branchData.country_code);
                let options = keviApi.getOptions("branches", "PUT", branchData);
                this.$http(options).then(
                    (_response) => {
                        this.savingBranch = false;
                        this.loadBranches();
                        this.resetBranchForm();
                        this.resetErrors();
                        this.close();
                        this.$store.dispatch("notification/success", {
                            text: this.t("places-branch_form-notification-saved"),
                            duration: 5000,
                        });
                    },
                    (_error) => {
                        this.$store.dispatch("notification/warning", {
                            text: this.t("places-branch_form-notification-fill_fields"),
                            duration: 5000,
                        });
                    }
                );
            }
        },
        resetBranchForm: function () {
            if (this.activeBranch) {
                let activeBranch =
                    this.$store.getters["placeDetails/getActiveBranch"];
                this.branchForm = Object.assign(
                    _.cloneDeep(defaultForm),
                    activeBranch,
                );
            } else {
                this.branchForm = _.cloneDeep(defaultForm);
                this.branchForm.country_code = this.defaultBranchCountry;
            }
            if (!this.branchForm.country_code) {
                this.branchForm.country_code = this.defaultBranchCountry;
            }
        },
        countryLabelFor(countryCode) {
            const option = this.countryOptions.find((country) => country.value === countryCode);
            return option ? option.label : countryCode;
        },
    },
    watch: {
        modalVisible() {
            if (this.modalVisible === "branch-form") {
                this.$refs.branchForm.open();
            } else {
                this.$refs.branchForm.close();
            }
        },
        activeBranch() {
            this.resetBranchForm();
        },
        "branchForm.country_code"(newCode) {
            if (newCode !== "BR") {
                this.errors.state = null;
                this.errors.zip_code = null;
            }
        },
    },
};
</script>
